// Epic 7 re-skin (ADO-903) — the mobile/tablet (<1024px) filters drawer: the
// SAME FilterPanel the desktop rail carries, in a left-anchored panel over a
// scrim with a sticky "Apply filters" footer (filtering is already live; the
// button just closes the drawer — prototype Drawer anchor=left parity).
//
// Extracted from CategoryListing so the committed-search results (ProductSearch)
// reuse the identical drawer chrome rather than duplicating it.
//
// a11y: role="dialog" aria-modal, Escape + backdrop-click + Close button all
// dismiss (WAI-ARIA dialog pattern; backdrop click is a pointer-only convenience
// on top of the keyboard paths). PR #139 review: it now genuinely backs the
// aria-modal promise via the shared `useFocusTrap` — focus moves into the drawer
// on open, Tab/Shift+Tab are trapped within it (the page behind the scrim is
// inert to AT), and focus returns to the trigger on close. It also locks body
// scroll while open (same contract as the design-system Modal) so the page can't
// scroll under the scrim.

import { useEffect, useRef, type ReactNode } from 'react';
import { Button, Icon, useFocusTrap } from '../../design-system';
import styles from './FiltersDrawer.module.css';

export interface FiltersDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Drawer body — the host's FilterPanel. */
  readonly children: ReactNode;
}

export function FiltersDrawer({ open, onClose, children }: FiltersDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  // Trap Tab within the drawer, focus it on open, and restore focus to the trigger
  // on close (PR #139 review) — the same mechanism the design-system Modal uses.
  const trapTab = useFocusTrap(open, drawerRef);

  // Escape closes the drawer (overlay click closes it too).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Claim the Escape (preventDefault) so a layer listening further up the
      // bubble chain — the upsell toast, on `window` — does not ALSO dismiss on
      // it. The drawer lives on the listing pages where toasts fire, so this is
      // the most real cross-talk case.
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open (same contract as the design-system Modal) so the
  // page behind the scrim can't scroll under the drawer (wheel / PgDn); restore the
  // previous inline value on close (PR #139 review).
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    // Backdrop click-to-dismiss is a pointer-only convenience: the drawer is
    // keyboard-dismissible via Escape (document listener above) and the Close
    // button, per the WAI-ARIA dialog pattern.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.drawerOverlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* The onKeyDown implements the dialog's Tab focus-trap (WAI-ARIA dialog
          pattern); a keydown handler on the role="dialog" container is the intended
          structure. Escape is handled by the document listener above. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        tabIndex={-1}
        className={styles.drawer}
        onKeyDown={trapTab}
      >
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>Filters</h2>
          <button
            type="button"
            className={styles.drawerClose}
            aria-label="Close filters"
            onClick={onClose}
          >
            <Icon name="x" size={20} />
          </button>
        </div>
        <div className={styles.drawerBody}>{children}</div>
        <div className={styles.drawerFooter}>
          <Button fullWidth onClick={onClose}>
            Apply filters
          </Button>
        </div>
      </div>
    </div>
  );
}
